import * as React from 'react';
import get from 'lodash.get';
import { FlexBox, Box, Typography, Button, Logo, Grid, MuiBox } from '@kis-technologies/kis-ui';
import { useNavigate } from 'react-router-dom';
import { selectors as hardwareSelectors, actions as hardwareActions, PaymentDevice } from 'services/hardware';
import { selectors as cartSelectors, actions as cartActions, Cart } from 'services/cart';
import { selectors as kioskSelectors, KioskHardware } from 'services/kiosk';
import { selectors as ticketingSelectors, actions as ticketingActions } from 'services/ticketing';
import { selectors as salesErrorSelectors } from 'services/salesErrors';
import { useActions } from 'hooks/useActions';
import { useSelector } from 'react-redux';
import { formatCurrency, formatText } from 'utils/stringFormat';
import { ActionOverlay } from 'components/ActionOverlay';
import { Footer } from 'components/Footer';
import { LoadingOverlay } from 'components/LoadingOverlay';
import { ProcessingOverlay } from 'components/ProcessingOverlay';
import { USER_ERROR } from 'constants/hardware';
import { selectors as configSelectors, Configuration, LanguageField, Message } from 'services/configuration';
import { ReactComponent as Payment } from 'assets/credit_card_payment.svg';
import { ReactComponent as ContactlessCard } from 'assets/contact_less_payment.svg';
import { ReactComponent as DinersCard } from 'assets/diners_card_payment.svg';
import { ReactComponent as DiscoverCard } from 'assets/discover_card_payment.svg';
import { ReactComponent as AmexCard } from 'assets/amex_card_payment.svg';
import { ReactComponent as MasterCard } from 'assets/master_card_payment.svg';
import { ReactComponent as VisaCard } from 'assets/visa_card_payment.svg';
import { ReactComponent as GooglePay } from 'assets/google_pay_payment.svg';
import { ReactComponent as ApplePay } from 'assets/apple_pay_payment.svg';
import DevicePayment from 'assets/device_payment_icon.png';
import { useStyles } from './styled';
import { useTranslation } from 'react-i18next';
import { selectors as mediaSelectors } from 'services/media';

const PurchasePaymentView = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  // Actions and Selectors
  const { paymentSale, resetSaleData, paymentCancel, snapPicture, resetCancelError } = useActions(hardwareActions);
  const { purchaseCart, releaseCart, resetPurchaseError, resetCart } = useActions(cartActions);
  const { resetTicketing } = useActions(ticketingActions);
  const { status, saleData, loading, voidError, cancelLoading, cancelSuccess, cancelError }: PaymentDevice =
    useSelector(hardwareSelectors.selectPaymentDevice);
  const cart = useSelector(cartSelectors.selectCart) as Cart;
  const totalToCharge = useSelector(cartSelectors.selectCartTotalToCharge);
  const purchaseError = useSelector(cartSelectors.selectPurchaseError);
  const salesError = useSelector(salesErrorSelectors.selectError);
  const purchased = useSelector(cartSelectors.selectPurchased);
  const config = useSelector(configSelectors.selectConfiguration) as Configuration;
  const kiosk = useSelector(kioskSelectors.selectKiosk);
  const backgroundImage = useSelector(mediaSelectors.selectBackgroundImage) || [];
  const logoImage = useSelector(mediaSelectors.selectLogoImage) || [];
  const ticketingPath = useSelector(ticketingSelectors.selectTicketingConfigPath);

  const messaging: Message = get(config, `messaging.${i18n.language}.purchasePaymentPage`, {});
  const flowTemplate = get(config, 'configuration.flowTemplate') as string;
  const languageSettings: LanguageField[] = get(config, 'configuration.languageSettings', []);
  const support = get(config, `${ticketingPath}.supportName.${i18n.language}`) || t('commonText.support');
  const allowPaymentCancelation = get(config, `${ticketingPath}.allowPaymentCancelation`);
  const fairTicketType = get(cart, 'products[0].items[0].type', '');
  const clientId = get(kiosk, 'venue.clientId');
  const previewAllowCheckOut = get(kiosk, 'configuration.kioskPreview.allowCheckOut');
  const bgImageName = get(backgroundImage[0], 's3Name') || 'default.png';
  const imageBaseUrl = `${process.env.REACT_APP_S3_ASSETS}/${clientId}`;
  const backgroundUrl = `${imageBaseUrl}/background_images/${bgImageName}`;
  const isPreviewMode = sessionStorage.getItem('mode') === 'preview';
  const allowCheckOut = !isPreviewMode || (isPreviewMode && previewAllowCheckOut);
  const palette = get(config, 'configuration.globalSettings.palette');
  const fonts = get(config, 'configuration.globalSettings.fonts');
  const cartLabel = get(config, `configuration.labels.cart.${i18n.language}`) || t('cart.cartLabel');

  const classes = useStyles();

  const backgroundStyle = () => {
    if (config && palette) {
      return { background: `${palette.darkPrimary}e5` };
    }
    return {};
  };

  // On Mount - call Payment Sale: hits payment device hardware
  React.useEffect(() => {
    if (flowTemplate !== 'fair' && !cart && !purchased) {
      goBackToCart();
    }
    if (flowTemplate === 'fair') {
      snapPicture();
    }
    if (totalToCharge > 0 && !saleData) {
      paymentSale();
    }

    return () => resetCancelError();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // On Update of purchased
  React.useEffect(() => {
    // if purchased is true, adding a delay of 3 sec to show the processing animation to show
    // then, re-route to the delivery page
    if (purchased) {
      setTimeout(() => {
        navigate('/ticketing/delivery');
      }, 3000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchased]);

  React.useEffect(() => {
    if (cancelSuccess && [USER_ERROR].includes(status.code) && !loading) {
      goBack();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cancelSuccess, status.code, loading]);

  const getPaymentImgUrl = () => {
    if (kiosk) {
      const paymentDevice = kiosk.hardware.find((device: KioskHardware) => device.type === 'paymentDevice');
      if (paymentDevice && paymentDevice.imageUrl && paymentDevice.imageUrl.primary) {
        return paymentDevice.imageUrl.primary;
      }
    }
    return undefined;
  };

  const cancelText = () => {
    if (flowTemplate === 'fair') {
      return 'Go Back'.toUpperCase();
    }
    return t('purchasePayment.backToCart', { cartLabel });
  };

  const onLeave = () => {
    if (cart) releaseCart(cart.releaseParams);
    resetTicketing();
    resetSaleData();
    resetPurchaseError();
    navigate('/ticketing/attract');
  };

  const goBack = () => {
    resetSaleData();
    resetPurchaseError();
    if (flowTemplate === 'fair') {
      resetCart();
      navigate('/ticketing/selection');
    } else {
      goBackToCart();
    }
  };

  const goBackToCart = () => {
    navigate('/ticketing/cart', { state: { from: location.pathname } });
  };

  return (
    <>
      {cancelSuccess && loading && <LoadingOverlay style={{ zIndex: '2147483648' }} />}
      {/* when there is an error and hardware is not loading, show action overlay with hardware message set dynamically and action to retry payment action */}
      {[USER_ERROR].includes(status.code) && !loading && !cancelLoading && (
        <>
          <ActionOverlay
            flow={flowTemplate}
            style={backgroundStyle()}
            title={
              <Box margin="0 0 5" width={710}>
                <Typography fontSize={64} fontWeight={600} align="center" sx={{ lineHeight: 1 }}>
                  {formatText(t('commonText.somethingWentWrong'), flowTemplate)}
                </Typography>
              </Box>
            }
            description={
              <Box width={710}>
                <Typography fontSize={30} align="center">
                  {status.message}
                </Typography>
              </Box>
            }
            confirmText={formatText(t('commonText.tryAgain'), flowTemplate)}
            onConfirm={() => {
              resetSaleData();
              resetPurchaseError();
              resetCancelError();
              paymentSale();
            }}
            cancelText={cancelText()}
            onCancel={() => goBack()}
          />
        </>
      )}
      {purchaseError && !loading && (
        <ActionOverlay
          flow={flowTemplate}
          style={backgroundStyle()}
          title={
            <Box margin="0 0 5" width={720}>
              <Typography fontSize={64} fontWeight={600} align="center" sx={{ lineHeight: 1 }}>
                {formatText(t('commonText.somethingWentWrong'), flowTemplate)}
              </Typography>
            </Box>
          }
          description={
            <Box width={810}>
              <Typography fontSize={30} align="center">
                {purchaseError}
                <br />
                {voidError ? t('saleError.requestRefund', { support }) : t('saleError.notCharged')}
              </Typography>
            </Box>
          }
          confirmText={formatText(t('commonText.tryAgain'), flowTemplate)}
          onConfirm={() => {
            resetSaleData();
            resetPurchaseError();
            resetCancelError();
            paymentSale();
          }}
          cancelText={formatText(t('commonText.leave'), flowTemplate)}
          onCancel={onLeave}
        />
      )}

      {/* when there is saleData returned from the payment device hardware, open up processing overlay to call API layer with payment info */}
      {allowCheckOut && saleData && (
        <ProcessingOverlay
          error={purchaseError || salesError}
          processed={purchased}
          processAction={purchaseCart}
          title={formatText(t('purchasePayment.paymentCompleteMessage'), flowTemplate)}
        />
      )}

      {/* For zero dollar transactions, directly show the purchase overlay*/}
      {allowCheckOut && totalToCharge === 0 && (
        <ProcessingOverlay
          error={purchaseError || salesError}
          processed={purchased}
          processAction={purchaseCart}
          title={formatText(t('purchasePayment.processingTicket'), flowTemplate)}
        />
      )}

      {!allowCheckOut && (
        <ActionOverlay
          flow={flowTemplate}
          style={backgroundStyle()}
          height={450}
          title="Can't Continue in Preview Mode"
          description="Checkout is disabled in preview mode for this kiosk."
          confirmText={cancelText()}
          onConfirm={() => goBack()}
        />
      )}

      {totalToCharge > 0 && flowTemplate !== 'fair' ? (
        <>
          <Grid className={classes.paymentOuterContainer}>
            <Grid container className={classes.paymentInnerContainer}>
              {/* Left Portion of Card with Text */}
              <Grid item lg={12} xl={6} className={classes.paymentTextContainer}>
                <MuiBox className={classes.messageHeader}>
                  {(messaging && messaging.header && (
                    <FlexBox margin="0 0 8" direction="column">
                      <Typography fontSize={100} fontWeight={600} sx={{ lineHeight: 1.1 }}>
                        {messaging.header}
                      </Typography>
                    </FlexBox>
                  )) || (
                    <FlexBox margin="0 0 8" direction="column" width="640px">
                      <Typography fontSize={100} fontWeight={600} sx={{ lineHeight: 1.1 }}>
                        {t('purchasePayment.payNow')}
                      </Typography>
                    </FlexBox>
                  )}
                  <Box flexOverride="none">
                    <Typography fontSize={28}>
                      {(messaging && messaging.message) || t('purchasePayment.instructionMessage')}
                    </Typography>
                  </Box>
                </MuiBox>
                <MuiBox className={classes.totalBorderLine} />
                <FlexBox margin="4 0 0" justify="end">
                  <Typography fontSize={40} fontWeight={600}>
                    {t('commonText.total')}: {formatCurrency(totalToCharge, {}, languageSettings)}
                  </Typography>
                </FlexBox>
              </Grid>
              {/* Right Portion of Card with Lottie Animation for Card Swipe */}
              <Grid item lg={12} xl={6} className={classes.paymentImageBlock}>
                <FlexBox fullHeight align="center" justify="center">
                  {!getPaymentImgUrl() && <Payment />}
                  {getPaymentImgUrl() && <img width={500} height={538} alt="url" src={getPaymentImgUrl()} />}
                </FlexBox>
              </Grid>
            </Grid>
          </Grid>
          {allowPaymentCancelation && (
            <Footer
              onPrevClick={paymentCancel}
              prevText={t('commonText.cancel')}
              isPrevDisabled={cancelLoading || Boolean(cancelError) || cancelSuccess}
              isPrevLoading={cancelLoading}
            />
          )}
        </>
      ) : null}

      {totalToCharge > 0 && flowTemplate === 'fair' ? (
        <FlexBox height="100%" overflow="hidden">
          <FlexBox fullWidth sx={{ backgroundImage: `url(${backgroundUrl})` }}>
            <FlexBox height="41%">
              <FlexBox fullWidth align="end" justify="center" height="30%" margin="0 0 8 0">
                {logoImage && logoImage.length > 0 ? (
                  <img alt="url" src={`${imageBaseUrl}/logo_images/${logoImage[0].s3Name}`} style={{ height: 97 }} />
                ) : (
                  <Logo />
                )}
              </FlexBox>
              <FlexBox fullWidth align="center" justify="center" margin="0 0 6 0">
                <Typography
                  color={palette?.primaryText}
                  sx={{ lineHeight: '1', fontFamily: fonts?.secondary, margin: '0 20px' }}
                  fontSize={125}
                  align="center"
                >
                  PLEASE PAY WITH
                  <br />
                  YOUR CREDIT CARD
                </Typography>
              </FlexBox>
              <FlexBox fullWidth align="start" justify="center" height="10%" margin="0 0 5 0">
                <Typography
                  color={palette?.primaryText}
                  fontSize={42}
                  sx={{ fontFamily: fonts?.primary, margin: '5px' }}
                  align="center"
                >
                  SELECTED COUPONS
                </Typography>
              </FlexBox>
              <FlexBox
                align="center"
                justify="center"
                fullWidth
                sx={{ backgroundColor: palette?.darkPrimary }}
                direction="column"
                height="200px"
              >
                <Typography fontSize={60} color={palette?.secondaryText}>
                  {formatCurrency(totalToCharge, {}, languageSettings)}
                </Typography>
                <Typography fontSize={42} color={palette?.secondaryText}>
                  {fairTicketType.toString().toUpperCase()}
                </Typography>
              </FlexBox>
            </FlexBox>
            <FlexBox justify="center" height="51%" sx={{ backgroundColor: 'white' }}>
              <FlexBox fullWidth justify="center" align="center" margin="6 0 0 0">
                <Typography
                  color={palette?.primaryText}
                  fontSize={42}
                  sx={{ fontFamily: fonts?.primary, margin: '0 25px' }}
                  align="center"
                >
                  PLEASE FOLLOW THE <br />
                  INSTRUCTIONS ON THE <br />
                  PAYMENT DEVICE
                </Typography>
              </FlexBox>
              <FlexBox align="center" justify="center">
                <Grid direction="row">
                  <Grid className={classes.paymentCardBlock}>
                    <div className={classes.paymentFirstDivision}>
                      <VisaCard />
                    </div>
                    <div className={classes.paymentSecondDivision}>
                      <MasterCard />
                    </div>
                  </Grid>
                  <Grid className={classes.paymentCardBlock}>
                    <div className={classes.paymentFirstDivision}>
                      <AmexCard />
                    </div>
                    <div className={classes.paymentSecondDivision}>
                      <DiscoverCard />
                    </div>
                  </Grid>
                  <Grid className={classes.paymentCardBlock}>
                    <div className={classes.paymentFirstDivision}>
                      <DinersCard />
                    </div>
                    <div className={classes.paymentSecondDivision}>
                      <ContactlessCard />
                    </div>
                  </Grid>
                  <Grid className={classes.paymentCardBlock}>
                    <div className={classes.paymentFirstDivision}>
                      <ApplePay />
                    </div>
                    <div className={classes.paymentSecondDivision}>
                      <GooglePay />
                    </div>
                  </Grid>
                </Grid>
                <Grid>
                  <img alt="Payment Device" src={DevicePayment} className={classes.paymentDeviceImage} />
                </Grid>
              </FlexBox>
              <FlexBox fullWidth justify="around" align="center">
                {allowPaymentCancelation && (
                  <Button
                    appearance="secondary"
                    customColor={palette?.secondaryText}
                    customSize="x-large"
                    onClick={paymentCancel}
                    disabled={cancelLoading || Boolean(cancelError) || cancelSuccess}
                    isLoading={cancelLoading}
                    width={1000}
                  >
                    <Typography fontSize={42} sx={{ fontFamily: fonts?.primary, margin: '0 25px' }} align="center">
                      {cancelError ? cancelError.toUpperCase() : 'CANCEL'}
                    </Typography>
                  </Button>
                )}
              </FlexBox>
            </FlexBox>
          </FlexBox>
        </FlexBox>
      ) : null}
    </>
  );
};

export default PurchasePaymentView;
